import { hasModule } from "csi/_base/modules";
import comm from "csi/comm";
import Key from "csi/core/Key";
import reflection from "csi/reflection";
import ReflectionClass from "csi/reflection/ReflectionClass";
import siteDefaultLocale from "csi/siteDefaultLocale";
import TabsView from "csi/tabs/View";
import types from "csi/types";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import { reject } from "lodash";

// Do not allow configuring cards where there is OOTB logic
const ATTRIBUTE_CARD_RESTRICT_TYPES = {
	CamundaTask: 1,
	User: 1,
};

/**
 * Restricted formats for data point filter below
 */
const _restrictedFormats = new Set([
	types.FORMAT_BARCODE,
	types.FORMAT_IMAGE,
	types.FORMAT_RICH,
]);

/**
 * Restrict lists and rich text attributes from cards to keep from growing too big and/or performing poorly.
 */
export function canShowAttribute(key: Key): boolean {
	const ra = key.reflectionAttribute ;

	// name will be shown using node property rather than Id
	if ((key.id == xml.ATTR_NAME) && !key.path) {
		return false;
	}

	if (ra && _restrictedFormats.has(ra.getFormat())) {
		return false;
	}

	if (xml.attributeTypeIsContainer(ra.m_type)) {
		return false;
	}

	return true;
}

/**
 * Return true if the given Business Object node can setup attribute cards
 */
export function canSetupAttributeCards(rc: ReflectionClass): boolean {
	const type = rc.getType();

	return (rc.isPrimaryClass || rc.isAbstractClassOfDomainClasses) &&
		!ATTRIBUTE_CARD_RESTRICT_TYPES[type] &&
		!rc.isDerivedFrom("TableReport") &&
		!type.startsWith("_");
}

/**
 * Return true if the given Business Object node can setup filter attributes
 */
export function canSetupFilterAttributes(rc: ReflectionClass): boolean {
	const type = rc.getType();

	return rc.isPrimaryClass &&
		!type.startsWith("_") &&
		!rc.isSingleton();
}

/**
 * Customize layout of BusinessObject layout view
 */
export async function customizeLayout(tabsView: TabsView, node: C8Node): Promise<void> {
	let wbsEnabled = false;

	const className = node.RootClassName;
	const rc = reflection.getClass(className);
	const hasFormDef = xml.isValidURL(node.CreateFormDefinition);
	const wbsEnabledUrl = node.ConfigurableAttributes[className + "/WbsEnabled"];
	const promise1 = siteDefaultLocale.initialize();
	const promise2 = comm.getNodeRequest(wbsEnabledUrl)?.go()
		.then((response) => {
			wbsEnabled = response.getResultNode()[reflection.ATTR_VALUE];
		});

	try {
		await Promise.all([promise1, promise2]);
	} catch (ex) {
		// ignore error
	}

	// remove tabs that do not apply to current Business Object
	const inheritsAWH = hasModule("Camunda") && isConcreteAdvancedWorkflowHolder(rc);
	const removeValidationTabs = !inheritsAWH && !hasFormDef && !rc.isMaster() && !wbsEnabled;
	const omitIds: Record<string, boolean> = {};

	if (removeValidationTabs) {
		omitIds.ValidationRules = true;
		omitIds.ValidationRuleGroups = true;
	}

	if (rc.abstract || !rc.isImageContainer) {
		omitIds.ImageLabels = true;
	}

	if (!canSetupAttributeCards(rc)) {
		omitIds.AttributeCards = true;
		omitIds.MasterRevisionAttributeCards = true;
	}

	if (!canSetupFilterAttributes(rc)) {
		omitIds.FilterAttributes = true;
	}

	tabsView.tabs = reject(tabsView.tabs, (tab) => omitIds[tab.m_idView]);
}

/**
 * Return true if class supports advanced workflow
 */
export function isConcreteAdvancedWorkflowHolder(rc: ReflectionClass): boolean {
	const awhClass = reflection.getClass("AdvancedWorkflowHolder");
	return awhClass && !rc.abstract && rc.isDerivedFrom(awhClass);
}
